// Client helpers for Excalidraw scene image files.  Bytes are offloaded to
// MinIO via the API's pre-signed URL endpoints (api/src/routes/scene-files.ts),
// mirroring how attachments work.  Authorization is by scene access, so these
// also work for share recipients.

function authHeaders(token: string): HeadersInit {
  return { Authorization: `Bearer ${token}` };
}

function dataURLToBlob(dataURL: string): Blob {
  const [head, body] = dataURL.split(",", 2);
  const isBase64 = /;base64$/i.test(head ?? "") || /;base64;/i.test(head ?? "");
  const mimeMatch = /^data:([^;,]+)/i.exec(head ?? "");
  const mime = mimeMatch?.[1] ?? "application/octet-stream";
  if (isBase64) {
    const bin = atob(body ?? "");
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  }
  return new Blob([decodeURIComponent(body ?? "")], { type: mime });
}

function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onerror = () => reject(fr.error);
    fr.onload = () => resolve(fr.result as string);
    fr.readAsDataURL(blob);
  });
}

/** Upload an Excalidraw image file's bytes to MinIO.  Idempotent on the
 *  server (UPSERT) — safe to retry. */
export async function uploadSceneFile(
  token: string,
  sceneId: string,
  fileId: string,
  dataURL: string,
  mimeType: string,
): Promise<void> {
  const blob = dataURLToBlob(dataURL);
  const r = await fetch(
    `/api/scenes/${encodeURIComponent(sceneId)}/files/${encodeURIComponent(fileId)}/upload-url`,
    {
      method: "POST",
      headers: { ...authHeaders(token), "Content-Type": "application/json" },
      body: JSON.stringify({ mimeType, sizeBytes: blob.size }),
    },
  );
  if (!r.ok) throw new Error(`scene-file upload-url ${r.status}`);
  const { url } = (await r.json()) as { url: string };
  const put = await fetch(url, { method: "PUT", body: blob });
  if (!put.ok) throw new Error(`scene-file put ${put.status}`);
}

/** Fetch an image file's bytes as a dataURL so it can be fed to Excalidraw's
 *  addFiles().  Returns null if the file isn't registered yet (the uploader
 *  may still be in-flight) so the caller can retry. */
export async function fetchSceneFileAsDataURL(
  token: string,
  sceneId: string,
  fileId: string,
): Promise<{ dataURL: string; mimeType: string } | null> {
  const r = await fetch(
    `/api/scenes/${encodeURIComponent(sceneId)}/files/${encodeURIComponent(fileId)}/download-url`,
    { headers: authHeaders(token) },
  );
  if (r.status === 404) return null;
  if (!r.ok) throw new Error(`scene-file download-url ${r.status}`);
  const { url, mimeType } = (await r.json()) as {
    url: string;
    mimeType: string;
  };
  const got = await fetch(url);
  if (!got.ok) throw new Error(`scene-file get ${got.status}`);
  const dataURL = await blobToDataURL(await got.blob());
  return { dataURL, mimeType: mimeType || "application/octet-stream" };
}
